import { NodeType } from 'shared/types/ingestion'
import {
  getFileRequest,
  getFilesRecursive2,
  FileStructureProcessor,
  FolderData,
  GDriveFileWithPath,
} from 'infrastructure/googleDrive'
import { ILeafRequest, INode } from 'shared/types/tree'
import { createIdMap, NodeIdMap } from 'components/version_mapping/utility'

// Recursively creates a FolderData structure from a Kiddom tree node
// Note that it only creates paths that have attachmentList nodes
function createBulkUploadFolderStructureFromKiddomTree(
  tree: INode,
): FolderData {
  // Base case: Create folder data for the current node
  const folderData: FolderData = {
    name: tree.title,
    subfolders: [],
    files: [],
  }

  // If the current node has sub-nodes, recursively build their structure
  tree.nodes.forEach((node) => {
    if (node.title) {
      const subfolder = createBulkUploadFolderStructureFromKiddomTree(node) // Recursively call for sub-nodes
      // only append the subfolder if it has a file
      if (subfolder.subfolders.length || subfolder.files.length) {
        folderData.subfolders.push(subfolder) // Add the subfolder to the current folder
      }
    }
  })

  if (tree.type === 'attachments_list') {
    folderData.files.push({
      mimeType: 'application/json',
      name: 'metadata.json',
      content: JSON.stringify({
        nodeId: tree.id,
        treeId: tree.tree_id,
        version: tree.version,
      }),
    })
  }

  return folderData
}

export function exportKiddomTreeToGoogleDriveFolder(
  tree: INode,
  accessToken: string,
  setProgress: (progress: number, total?: number, completed?: number) => void,
): Promise<string> {
  const folderData = createBulkUploadFolderStructureFromKiddomTree(tree)
  const fs = new FileStructureProcessor(folderData, accessToken, setProgress)
  return fs.createFoldersAndFiles()
}

/**
 * Get the parent directory path from a file's namePath
 * e.g., "/folder1/subfolder/file.pdf" => "/folder1/subfolder"
 */
function getParentPath(namePath: string): string {
  const parts = namePath.split('/')
  parts.pop() // Remove filename
  return parts.join('/') || '/'
}

/**
 * Get the grandparent directory path from a file's namePath
 * e.g., "/folder1/subfolder/file.pdf" => "/folder1"
 */
function getGrandparentPath(namePath: string): string {
  const parentPath = getParentPath(namePath)
  return getParentPath(parentPath)
}

/**
 * Get the folder name from a path
 * e.g., "/folder1/subfolder" => "subfolder"
 */
function getFolderName(path: string): string {
  const parts = path.split('/').filter(Boolean)
  return parts[parts.length - 1] || ''
}

function createAttachmentLeafFromGDriveFile(
  file: GDriveFileWithPath,
  params?: object,
) {
  return {
    type: 'content:google',
    body: {
      file: {
        name: file.name,
        url: 'https://drive.google.com/file/d/' + file.id,
      },
      file_id: file.id,
    },
    ...params,
  }
}

// helper function to remove prepended numbers from file names
function normalizeFileName(name: string) {
  return name.replace(/^\d+\s*/, '').trim()
}

// helper function to sort attachment leaves by name and then remove prepended numbers
function prepareAttachmentLeaves(attachmentLeaves: ILeafRequest[]) {
  return attachmentLeaves
    .sort((a, b) =>
      a.body.file.name
        .toLowerCase()
        .localeCompare(b.body.file.name.toLowerCase()),
    )
    .map((leaf: ILeafRequest) => {
      leaf.body.file.name = normalizeFileName(leaf.body.file.name)
      return leaf
    })
}

const SUPPORTED_MIME_TYPES = [
  'application/vnd.google-apps.document', // google docs
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/pdf', // pdf
]

export class NewAttachmentsProcessor {
  accessToken: string
  files: GDriveFileWithPath[]
  idMap: NodeIdMap
  /** Map of directory path to metadata.json file */
  metadataByPath: Map<string, GDriveFileWithPath>

  constructor(accessToken: string, files: GDriveFileWithPath[], tree: INode) {
    this.accessToken = accessToken
    this.files = files
    this.idMap = createIdMap(tree)

    // Build a map of directory paths to their metadata.json files
    this.metadataByPath = new Map()
    for (const file of files) {
      if (file.name === 'metadata.json') {
        const dirPath = getParentPath(file.namePath)
        this.metadataByPath.set(dirPath, file)
      }
    }
  }

  static async create(
    accessToken: string,
    folderId: string,
    tree: INode,
  ): Promise<NewAttachmentsProcessor> {
    const files = await getFilesRecursive2(accessToken, folderId)
    return new NewAttachmentsProcessor(accessToken, files, tree)
  }

  private getNewPdfs() {
    return this.files
      .filter((file) => SUPPORTED_MIME_TYPES.includes(file.mimeType))
      .map((file) => ({
        ...file,
        parentPath: getParentPath(file.namePath),
        grandparentPath: getGrandparentPath(file.namePath),
        parentName: getFolderName(getParentPath(file.namePath)),
        grandparentName: getFolderName(getGrandparentPath(file.namePath)),
      }))
  }

  async getNewAttachmentLeavesAndLists() {
    const newPdfs = this.getNewPdfs()
    const newAttachmentLeaves: ILeafRequest[] = []
    const compositeIdToAttachmentLeaves: Record<string, ILeafRequest[]> = {}
    const skippedPdfs: GDriveFileWithPath[] = []

    await Promise.all(
      newPdfs.map(async (pdf) => {
        const parentMetadata = this.metadataByPath.get(pdf.parentPath)

        // if parent has metadata, add pdf to parent
        if (parentMetadata) {
          const fileReq = await (
            await getFileRequest(this.accessToken, parentMetadata.id)
          ).json()
          const existingLeaves = this.idMap[fileReq.nodeId].node.leaves

          const newLeaf = createAttachmentLeafFromGDriveFile(pdf, {
            parentName: pdf.parentName || 'Attachments',
            grandparentName: pdf.grandparentName || 'root',
            parent_id: fileReq.nodeId,
            tree_id: fileReq.treeId,
            version: fileReq.version,
          })

          if (
            !existingLeaves?.find(
              (l) =>
                l.body.file.name === normalizeFileName(newLeaf.body.file.name),
            )
          ) {
            newAttachmentLeaves.push(newLeaf)
          } else {
            skippedPdfs.push(pdf)
          }
          return
        }

        // if parent does not have metadata, check grandparent
        const grandParentMetadata = this.metadataByPath.get(pdf.grandparentPath)

        if (!grandParentMetadata) {
          throw Error('missing metadata')
        }

        // if grandparent has metadata, create new attachment list under grandparent and add pdf to it
        const fileReq = await (
          await getFileRequest(this.accessToken, grandParentMetadata.id)
        ).json()
        const grandparentName = pdf.grandparentName || 'root'
        const attachmentListName = pdf.parentName || 'Attachments'
        const compositeId = `${fileReq.treeId}:${fileReq.version}:${fileReq.nodeId}:${attachmentListName}:${grandparentName}`
        if (!Object.keys(compositeIdToAttachmentLeaves).includes(compositeId))
          compositeIdToAttachmentLeaves[compositeId] = []
        compositeIdToAttachmentLeaves[compositeId].push(
          createAttachmentLeafFromGDriveFile(pdf),
        )
      }),
    )

    const newAttachmentLists = Object.keys(compositeIdToAttachmentLeaves).map(
      (compositeId) => {
        const [
          treeId,
          version,
          nodeId,
          attachmentListName,
          ...grandparentNames
        ] = compositeId.split(':')
        const newAttachmentList = {
          title: attachmentListName,
          type: NodeType.AttachmentsList,
          leaves: prepareAttachmentLeaves(
            compositeIdToAttachmentLeaves[compositeId],
          ),
        }

        const newNode = {
          parentName: grandparentNames.join(':'),
          tree_id: treeId,
          version: version,
          id: nodeId,
          ...newAttachmentList,
        }
        return newNode
      },
    )

    return {
      newAttachmentLeaves: prepareAttachmentLeaves(newAttachmentLeaves),
      newAttachmentLists,
      skippedPdfs,
    }
  }
}
